<script setup>
import { computed, ref } from 'vue'
import { DxList } from 'devextreme-vue/list'
import { getI18n } from '@/plugins/i18n'
import { planningApi } from '@/apis/planning.api'
import { usePlanningStore } from '@/stores/planning.store'
import { useDetectDevice } from '@/composables/device'
import { useODataSource } from '@/composables/data-grid'
import { useChips } from '@/composables/chips'
import { ROI_COMPARISON_LIMITS, useRoiComparisonFormat, useRoiComparisonLimits } from '@/composables/roi-comparison'

import ListItem from '@/components/shared/ListItem.vue'
import EquipmentListItem from '@/components/shared/EquipmentListItem.vue'
import StaffListItem from '@/components/shared/StaffListItem.vue'
import SearchInput from '@/components/shared/SearchInput.vue'

const props = defineProps({
  type: {
    type: Number,
    required: true,
  },
  modelValue: {
    type: Array,
    required: true,
  },
})

const emit = defineEmits(['update:modelValue'])

const i18n = getI18n()
const planningStore = usePlanningStore()
const { isMobile } = useDetectDevice()
const { getCompanyChips } = useChips()
const { TYPE } = useRoiComparisonFormat()

const search = ref('')
const dataSource = computed(() => useODataSource(planningApi.getAbsoluteUrl() + planningStore.getRoiComparisonResourceUrl(props.type), {
  searchExpr: 'search_field',
  filter: ['archived', '=', false],
}))

const { isFull, limitColor, limitLabel } = useRoiComparisonLimits(() => props.modelValue)
const isSelected = (id) => props.modelValue.some(item => item.id === id)
const isDisabled = (item) => !isSelected(item.id) && isFull.value

const rowComponent = computed(() => ({
  [TYPE.EQUIPMENT]: EquipmentListItem,
  [TYPE.STAFF]: StaffListItem,
  [TYPE.COMPANY]: ListItem,
})[props.type])

const rowProps = (item) => ({
  [TYPE.EQUIPMENT]: { equipment: item },
  [TYPE.STAFF]: { staff: item },
  [TYPE.COMPANY]: { label: item.name, captionChips: getCompanyChips(item) },
})[props.type]

const onSearch = (value) => {
  search.value = value ?? ''
  dataSource.value.searchValue(search.value)
  dataSource.value.load()
}

const onToggle = (item) => {
  if (isSelected(item.id)) emit('update:modelValue', props.modelValue.filter(selected => selected.id !== item.id))
  else if (!isFull.value) emit('update:modelValue', [...props.modelValue, item])
}
</script>

<template>
  <div>
    <div class="row items-center no-wrap q-gutter-x-sm">
      <search-input :model-value="search" class="col" @update:model-value="onSearch" />
      <span class="text-caption text-uppercase text-weight-bold text-grey-7">{{ i18n.t('roiCompare.selected') }}</span>
      <q-badge :color="limitColor" :label="limitLabel">
        <q-tooltip>{{ i18n.t('roiCompare.selectRange', { min: ROI_COMPARISON_LIMITS.MIN, max: ROI_COMPARISON_LIMITS.MAX }) }}</q-tooltip>
      </q-badge>
    </div>
    <q-separator class="q-mt-sm" />
    <dx-list
      :key="type"
      :data-source="dataSource"
      :height="isMobile ? 'calc(100vh - 24rem)' : '45vh'"
      page-load-mode="scrollBottom"
      item-template="item"
      selection-mode="none"
      :active-state-enabled="false"
    >
      <template #item="{ data: item }">
        <div>
          <component
            :is="rowComponent"
            v-bind="rowProps(item)"
            class="q-pa-sm"
            dense
            clickable
            :disabled="isDisabled(item)"
            @click="onToggle(item)"
          >
            <template #avatar>
              <q-checkbox
                :model-value="isSelected(item.id)"
                :disable="isDisabled(item)"
                size="sm"
                @click.stop
                @update:model-value="onToggle(item)"
              />
            </template>
          </component>
        </div>
      </template>
    </dx-list>
  </div>
</template>
